import * as React from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

import UserMenu from './UserMenu';
import { ROUTES } from '../constants/routes';
import SubMenuHeadLine from './SubMenuHeadLine';
import getModuleName from '../utils/getModuleName';
import isRoutePathExact from '../utils/isRoutePathExact';
import {
  reportsSubMenuLinks,
  settingsSubMenuLinks,
  franchiseSubMenuLinks,
} from '../constants/menu';
import useUserRole from '../hooks/useUserRole';

function formatTitle(raw: string | undefined) {
  if (!raw) return '';
  return raw
    .toLowerCase()
    .replace(/(^|\s)\S/g, t => t.toUpperCase());
}

const HeadLine: React.FC<{ NoSubMenu?: boolean }> = ({
  NoSubMenu = false,
}) => {
  const { pathname } = useLocation();

  const {
    isFranchiseAdmin,
    isSuperAdmin,
    isEmployee,
  } = useUserRole();

  const subMenu = React.useMemo(() => {
    switch (true) {
      case isRoutePathExact(pathname, ROUTES.SETTINGS):
        return (
          <SubMenuHeadLine
            basePath={ROUTES.SETTINGS}
            isSuperAdmin={isSuperAdmin}
            menuLinks={
              !isFranchiseAdmin
                ? settingsSubMenuLinks.filter(
                    link =>
                      link.name !== 'Messages' &&
                      // Inspector Message of the Day is configured by
                      // Super Admins only. Keyed on the route, not the
                      // label, so renaming the tab can't silently ungate it.
                      (link.linkTo !== 'message-day' ||
                        isSuperAdmin) &&
                      // Special Permission configures who automations
                      // assign work to -- Super Admins only, keyed on
                      // the route for the same reason as above.
                      (link.linkTo !== 'special-permission' ||
                        isSuperAdmin) &&
                      // Global Variables feed every inspection form's
                      // mappings -- Super Admins only, same as above.
                      (link.linkTo !== 'global-variables' ||
                        isSuperAdmin),
                  )
                : settingsSubMenuLinks.filter(
                    link =>
                      link.name !== 'Report Setup' &&
                      link.name !== 'Vocabulary' &&
                      link.linkTo !== 'message-day' &&
                      link.linkTo !== 'special-permission' &&
                      link.linkTo !== 'global-variables',
                  )
            }
          />
        );

      case isRoutePathExact(pathname, ROUTES.REPORTS):
        if (isEmployee || NoSubMenu) return null;
        return <SubMenuHeadLine menuLinks={reportsSubMenuLinks} />;

      case isRoutePathExact(
        pathname,
        ROUTES.FRANCHISE_CONFIGURATION,
      ):
        return <SubMenuHeadLine menuLinks={franchiseSubMenuLinks} />;

      case isRoutePathExact(pathname, ROUTES.INSPECTIONS):
        if (pathname.indexOf('client') > -1) return null;
        return <PageTitle>Pending Inspections</PageTitle>;

      case isRoutePathExact(pathname, ROUTES.REVIEWS):
        return <PageTitle>Pending Reviews</PageTitle>;

      case isRoutePathExact(pathname, ROUTES.WIKI):
        return <PageTitle>Employee Module Management</PageTitle>;

      default:
        return null;
    }
    // Role flags arrive from Redux; without them the memo could freeze on the
    // pre-auth value and hide role-gated tabs (Messages, Message Setup).
  }, [
    pathname,
    isSuperAdmin,
    isFranchiseAdmin,
    isEmployee,
    NoSubMenu,
  ]);

  const moduleName = React.useMemo(
    () => <PageTitle>{formatTitle(getModuleName())}</PageTitle>,
    [],
  );

  return (
    <Bar>
      <Left>{subMenu || moduleName}</Left>
      <Right>
        <UserMenu />
      </Right>
    </Bar>
  );
};

const Bar = styled.header`
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 var(--space-6);
  background: var(--neutral-0);
  border-bottom: 1px solid var(--neutral-200);
  position: sticky;
  top: 0;
  z-index: 5;
  gap: var(--space-3);

  @media only screen and (max-width: 840px) {
    padding: 0 var(--space-4);
  }

  @media only screen and (max-width: 540px) {
    padding: 0 var(--space-3);
    height: 56px;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
  flex: 1;
  overflow: hidden;

  @media only screen and (max-width: 840px) {
    margin-left: 56px;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-left: auto;
  flex-shrink: 0;

  @media only screen and (max-width: 540px) {
    gap: var(--space-2);
  }
`;

const PageTitle = styled.h1`
  font-size: var(--fs-xl);
  font-weight: var(--fw-semibold);
  color: var(--neutral-900);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  @media only screen and (max-width: 540px) {
    font-size: var(--fs-lg);
  }

  @media only screen and (max-width: 400px) {
    font-size: var(--fs-md);
  }
`;

export default HeadLine;
